import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Sidebar from "@/pages/sidebar/sidebar";
import ClientMainHeader from "@/pages/header";
import ConfirmTransferDialog from "./confirm-dialog";
import { getPaymentMethods } from "@/actions/payment-methods";
import { MainWrapper } from "@/pages/styled";
import { getAllAccounts } from "./actions";
// import PaySomeoneModal from "@/components/move-money/pay-someone-modal";
import { PaymentCard, PaySomeoneCard } from "./components/StyledCard";
import AddAccountModal from "@/components/move-money/add-account-modal";
import useGetClientInfo from "@/pages/dashboard/hooks/useGetClientInfo";
import { FeatureBalanceCard } from "@/pages/dashboard/styled";
import { LimitedAccountWarning } from "@/components/limited-account-warning";
import { MerchantProcessingBanner } from "@/components/merchant-processing-banner";
import { useAccountBalance } from "@/hooks/account/use-account-balance";
import { useAuth } from "@/providers/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight, Loader2 } from "lucide-react";
import { PaymentMethodList } from "@/components/payment-methods";
import PaymentMethods from "@/components/payment-methods/payment-method-list";
import { Button } from "@/components/ui/button-tw";
import { sweepQueries } from "@/queries/sweeps";
import { useGetPaymentMethods } from "@/hooks/payment/use-get-payment-methods";
import { capitalizeFirstLetter } from "@/utils/string-manipulation";
import { banks_logos } from "@/assets/images/banks";
import { cn } from "@/utils/classnames";
import api from "@/api/v1-jwt/config/api";
import SweepsActivityTable from "../invoices/components/sweeps-activity-table/table";
import dayjs from "dayjs";
import { useSweepsReports } from "@/queries/sweeps/sweeps-reports/list";

// Utility to parse query parameters
const getQueryParam = (search, param) => {
  const params = new URLSearchParams(search);
  return params.get(param);
};

// Utility to update query parameters
const setQueryParam = (search, param, value) => {
  const params = new URLSearchParams(search);
  if (value) {
    params.set(param, value);
  } else {
    params.delete(param);
  }
  return params.toString();
};

const ADMIN_TABS = new Set(["payment-methods", "sweeps"]);
const ALL_TABS = new Set(["transfer-deposit", "payment-methods", "sweeps"]);

const MoveMoney = () => {
  const history = useHistory();
  const location = useLocation();

  const { cognitoId, clientId, userRole } = useAuth();
  const isAdmin = userRole === "admin";
  const userId = isAdmin || userRole === "auth_user" ? clientId : cognitoId;

  const currTab = useMemo(() => {
    const tab = getQueryParam(location.search, "tab");
    if (isAdmin) return ADMIN_TABS.has(tab) ? tab : "payment-methods";
    return ALL_TABS.has(tab) ? tab : "transfer-deposit";
  }, [location.search, isAdmin]);

  const { settingData } = useSelector((state) => state.account);

  const [shortCut, setShortCut] = useState({
    label: "Move money",
    value: "move-money",
  });
  const [addMoneyOption, setAddMoneyOption] = useState(null);
  const [addMoneyAmount, setAddMoneyAmount] = useState("");
  const [withdrawMoneyOption, setWithdrawMoneyOption] = useState(null);
  const [withdrawMoneyAmount, setWithdrawMoneyAmount] = useState("");

  const [selectedContact, setSelectedContact] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [paymentType, setPaymentType] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentAccount, setPaymentAccount] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [currentWidth, setCurrentWidth] = useState(0);
  const [paymentList, setPaymentList] = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);

  // const [confirmAccountDetails, setConfirmAccountDetails] = useState(false);
  const [addAccountDetails, setAddAccountDetails] = useState(false);
  const clientInfo = useGetClientInfo();
  const {
    data: balance,
    isLoading: balanceLoading,
    error: balanceError,
  } = useAccountBalance();
  useEffect(() => {
    getPaymentMethodList();
    getAccountInfo();
  }, [userId]);

  const checkBalance = (value) => {
    if (value > balance.available_balance) {
      alert("you don't have suficient balance");
      return false;
    }
    return true;
  };

  const getAccountInfo = async () => {
    await getAllAccounts(userId).then((res) => {
      if (res) setAccountInfo(res);
    });
  };

  const getPaymentMethodList = async () => {
    await getPaymentMethods({ status: true, user_id: userId }).then((res) => {
      if (res && res.length > 0) {
        const updatedList = res.map((item) => {
          return {
            ...item,
            label: item.payment_method_id,
            value: item.payment_method_id,
          };
        });
        setPaymentList(updatedList);
      }
    });
  };

  const handleAddMoney = () => {
    setPaymentType("add_money");
    setPaymentAccount(addMoneyOption);
    setPaymentAmount(addMoneyAmount);
    if (addMoneyAmount && addMoneyOption) setConfirmDialog(true);
  };

  const handleWithdrawMoney = () => {
    if (!checkBalance(withdrawMoneyAmount)) {
      return;
    }
    setPaymentType("withdraw_money");
    setPaymentAccount(withdrawMoneyOption);
    setPaymentAmount(withdrawMoneyAmount);
    if (withdrawMoneyOption && withdrawMoneyAmount !== "")
      setConfirmDialog(true);
  };

  return (
    <div className="h-100">
      <Sidebar currentWidth={currentWidth} setCurrentWidth={setCurrentWidth} />
      <>
        <LimitedAccountWarning />
        <MerchantProcessingBanner />
        <MainWrapper>
          <ClientMainHeader
            title="Move Money"
            shortCut={shortCut}
            setShortCut={setShortCut}
          />
          <Tabs
            value={currTab}
            onValueChange={(tabValue) => {
              const newSearch = setQueryParam(location.search, "tab", tabValue);
              history.replace({
                pathname: location.pathname,
                search: newSearch,
              });
            }}
            className="h-full"
          >
            <TabsList
              className={cn(
                "grid w-full max-w-[600px]",
                isAdmin ? "grid-cols-2" : "grid-cols-3"
              )}
            >
              {!isAdmin && (
                <TabsTrigger value="transfer-deposit">
                  Transfer & Deposit
                </TabsTrigger>
              )}
              <TabsTrigger value="payment-methods">Payment Methods</TabsTrigger>
              <TabsTrigger value="sweeps">Sweeps</TabsTrigger>
            </TabsList>
            {!isAdmin && (
              <TabsContent
                value="transfer-deposit"
                className="[&[data-state='active']]:mt-12 flex flex-col justify-between [&[data-state='active']]:h-5/6 "
              >
                <div className="flex flex-col gap-6">
                  <FeatureBalanceCard
                    status={clientInfo?.baas_status}
                    balance={balance?.available_balance}
                    isLoading={balanceLoading}
                    error={balanceError}
                  />

                  <div className="flex flex-wrap gap-6 justify-center lg:justify-normal">
                    <PaymentCard
                      customSelectValue={addMoneyOption}
                      customSelectorSetValue={setAddMoneyOption}
                      customSelectorSelectList={paymentList}
                      customSelectorPlaceholder="Select account"
                      priceInputValue={addMoneyAmount}
                      priceInputSetValue={setAddMoneyAmount}
                      priceInputPlaceholder="Amount"
                      cardTitle="Add Money to Your Pocketbook"
                      primary_color={settingData?.primary_color}
                      accountInfo={accountInfo}
                      handleClick={handleAddMoney}
                      arrowDirection="down"
                      buttonText={"Submit"}
                      moneyOut={false}
                      has_amount
                    />
                    <PaymentCard
                      customSelectValue={withdrawMoneyOption}
                      customSelectorSetValue={setWithdrawMoneyOption}
                      customSelectorSelectList={paymentList}
                      customSelectorPlaceholder="Select account"
                      priceInputValue={withdrawMoneyAmount}
                      priceInputSetValue={setWithdrawMoneyAmount}
                      priceInputPlaceholder="Amount"
                      cardTitle="Withdraw Money from Your Pocketbook"
                      primary_color={settingData?.primary_color}
                      accountInfo={accountInfo}
                      handleClick={handleWithdrawMoney}
                      arrowDirection="up"
                      buttonText={"Submit"}
                      balance={balance}
                      moneyOut={true}
                      has_amount
                    />
                    <PaySomeoneCard
                      primary_color={settingData?.primary_color}
                      selectedContact={selectedContact}
                      setSelectedContact={setSelectedContact}
                      selectedAccount={selectedAccount}
                      setSelectedAccount={setSelectedAccount}
                      handleClick={() => setAddAccountDetails(true)}
                    />
                  </div>
                </div>
                <div
                  onClick={() => history.push("/transactions")}
                  className=" font-medium  cursor-pointer text-skin-primary flex items-center mt-6 pb-6 "
                >
                  View past transactions
                  <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </TabsContent>
            )}
            <TabsContent value="payment-methods">
              {/* <PaymentMethodList settingData={settingData} /> */}
              <div className="mt-16 pb-8 md:pb-16">
                <PaymentMethods />
              </div>
            </TabsContent>
            <TabsContent
              value="sweeps"
              className="[&[data-state='active']]:mt-12 flex flex-col justify-between [&[data-state='active']]:h-5/6 "
            >
              <div className="flex gap-6 max-w-[900px]">
                <SweepsSummaryCard />
                <SweepsCard />
              </div>
              <div className="mt-10 flex flex-col gap-y-2">
                <h3 className="font-semibold text-lg text-gray-800">
                  Sweep Activity
                </h3>
                <SweepsActivityTable />
              </div>
            </TabsContent>
          </Tabs>
        </MainWrapper>
      </>
      <ConfirmTransferDialog
        dialog={confirmDialog}
        setDialog={setConfirmDialog}
        type={paymentType}
        amount={paymentAmount}
        selectedAccount={selectedAccount}
        paymentAccount={paymentAccount}
        contact={selectedContact}
        account={accountInfo}
      />
      {/* <PaySomeoneModal
        open={confirmAccountDetails}
        onOpenChange={setConfirmAccountDetails}
        contactId={selectedContact.contact_id}
      /> */}
      <AddAccountModal
        open={addAccountDetails}
        onOpenChange={setAddAccountDetails}
        contactId={selectedContact?.contact_id}
        setPaymentType={setPaymentType}
        selectedAccount={selectedAccount}
        setSelectedAccount={setSelectedAccount}
        balance={balance}
      />
    </div>
  );
};

function SweepsSummaryCard() {
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const { data: accountBalance, isLoading: isAccountBalanceLoading } =
    useAccountBalance();

  const { data: latestSweep, isLoading: isLatestSweepLoading } =
    useSweepsReports(userId, {
      select: (data) => {
        if (!data || data.length === 0) return undefined;
        return data.reduce((latest, report) => {
          if (!report.initiated_on_date) return latest;
          if (
            !latest ||
            new Date(report.initiated_on_date) >
              new Date(latest.initiated_on_date)
          ) {
            return report;
          }
          return latest;
        });
      },
    });

  const { data: averageSweepAmount, isLoading: isAverageSweepAmountLoading } =
    useSweepsReports(userId, {
      select: (data) => {
        if (!data || data.length === 0) return undefined;
        const total = data.reduce((sum, report) => sum + report.sweep_total, 0);
        return total / data.length;
      },
    });

  return (
    <div className="bg-white p-6 border border-border rounded-xl shadow-sm min-w-[400px]">
      {isLatestSweepLoading ||
      isAverageSweepAmountLoading ||
      isAccountBalanceLoading ? (
        <div className="flex justify-center items-center">
          <Loader2 className="w-12 h-12 animate-spin" />
        </div>
      ) : (
        <>
          <h3 className="text-lg font-semibold text-gray-900">Summary</h3>
          <dl className="divide-y divide-gray-100 mt-4">
            <div className=" py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
              <dt className="text-sm font-medium text-gray-900 sm:col-span-2">
                Account Balance
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:mt-0">
                {accountBalance?.available_balance !== undefined &&
                accountBalance?.available_balance !== null
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(accountBalance?.available_balance)
                  : "N/A"}
              </dd>
            </div>
            <div className=" py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
              <dt className="text-sm font-medium text-gray-900 sm:col-span-2">
                Last Sweep
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700  sm:mt-0">
                {latestSweep
                  ? dayjs(latestSweep.initiated_on_date).format("MM/DD/YYYY")
                  : "N/A"}
              </dd>
            </div>
            <div className=" py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
              <dt className="text-sm font-medium text-gray-900 sm:col-span-2">
                Avg Sweep Amount
              </dt>
              <dd className="mt-1 text-sm/6 text-gray-700  sm:mt-0">
                {averageSweepAmount
                  ? new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(averageSweepAmount)
                  : "N/A"}
              </dd>
            </div>
          </dl>
        </>
      )}
    </div>
  );
}

function SweepsCard() {
  const history = useHistory();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;
  const sweepSettings = sweepQueries.settings.entity.useQuery(userId);
  const paymentMethods = useGetPaymentMethods({
    enabled: !!sweepSettings.data?.external_identifier?.linked_account_id,
  });

  const paymentMethod = useMemo(() => {
    if (
      paymentMethods.data &&
      sweepSettings.data?.external_identifier?.linked_account_id
    ) {
      const methodsArray = Array.isArray(paymentMethods.data)
        ? paymentMethods.data
        : Array.isArray(paymentMethods.data?.data)
        ? paymentMethods.data.data
        : [];

      return methodsArray.find(
        (pm) =>
          pm.payment_method_id ===
          sweepSettings.data?.external_identifier?.linked_account_id
      );
    }

    return null;
  }, [
    paymentMethods.data,
    sweepSettings.data?.external_identifier?.linked_account_id,
  ]);

  const getBankName = () => {
    if (!paymentMethod) return "";

    return `${
      paymentMethod.payment_method_nickname ||
      paymentMethod.institution_info?.institution_name ||
      paymentMethod.payment_method_data?.institution?.name ||
      "Unknown Institution"
    } ${capitalizeFirstLetter(
      paymentMethod.payment_method_nickname
        ? ""
        : paymentMethod.payment_method_data?.account?.subtype || ""
    )}`;
  };

  const isLoading = sweepSettings.isLoading || paymentMethods.isLoading;

  return (
    <div className="bg-white p-6 border border-border rounded-xl shadow-sm min-w-[400px]">
      {isLoading ? (
        <div className="flex justify-center items-center">
          <Loader2 className="w-12 h-12 animate-spin" />
        </div>
      ) : !paymentMethod ? (
        <>
          <h3 className="text-lg font-semibold text-gray-900">
            Automatic Sweeps: Effortless Transfers, On Your Terms
          </h3>
          <p className="text-gray-500">
            Set it and forget it - once enabled, balances transfer
            automatically. With Sweep reports, easily track sweep activity and
            reconcile invoices.
          </p>
          <Button
            className="mt-4"
            onClick={() => history.push("/sweeps/setup")}
          >
            Learn More
          </Button>
        </>
      ) : (
        <>
          <span
            className={cn(
              "inline-block rounded-full bg-gray-800 text-white text-xs font-semibold py-1 px-3 mb-4"
            )}
          >
            Sweep Account
          </span>
          <div className="flex gap-4 items-center mb-2">
            <div className="max-w-8 h-auto">
              <img src={banks_logos["default"]} alt={`Bank Logo`} />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mt-1">
              {getBankName()}
            </h3>
          </div>
          <p className="text-gray-500">
            ****{paymentMethod.payment_method_data?.account?.mask}
          </p>

          <p className="text-sm text-gray-500 mt-8">
            To cancel or ask questions about your sweep, click below.
          </p>
          <Button variant="secondary" className="mt-2" asChild>
            <a href="mailto:support@pocketbook.tech">Contact Us</a>
          </Button>
        </>
      )}
    </div>
  );
}

export default MoveMoney;
